import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

declare const bootstrap: any;
@Injectable({
    providedIn: 'root'
})
export class DevelopmentService {
    constructor(private fb: FormBuilder) { }
    public ReportDate: any = new Date();
    public formattedDate: any;
    public getDate() {
        const year = this.ReportDate.getFullYear();
        const month = String(this.ReportDate.getMonth() + 1).padStart(2, '0'); // Adding 1 to get correct month (as it's zero-based)
        const day = String(this.ReportDate.getDate()).padStart(2, '0');
        this.formattedDate = `${year}-${month}-${day}`;
        return this.formattedDate;
    }

    markFormGroupTouched(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach(controlName => {
            const control = formGroup?.get(controlName);
            if (control instanceof FormControl) {
                if (!control.touched) {
                    control.markAsTouched();
                    control.updateValueAndValidity();
                }
            } else if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            } else if (control instanceof FormArray) {
                this.markFormArrayTouched(control);
            }
        });
    }
    markFormArrayTouched(formArray: FormArray) {
        formArray.controls.forEach(control => {
            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            } else if (control instanceof FormArray) {
                this.markFormArrayTouched(control);
            }
        });
    }
    markFormGroupUntouched(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach(controlName => {
            const control = formGroup?.get(controlName);
            if (control instanceof FormControl) {
                if (control.touched) {
                    control.markAsUntouched();
                    control.updateValueAndValidity();
                }
            } else if (control instanceof FormGroup) {
                this.markFormGroupUntouched(control);
            } else if (control instanceof FormArray) {
                this.markFormArrayUntouched(control);
            }
        });
    }

    markFormArrayUntouched(formArray: FormArray) {
        formArray.controls.forEach(control => {
            if (control instanceof FormGroup) {
                this.markFormGroupUntouched(control);
            } else if (control instanceof FormArray) {
                this.markFormArrayUntouched(control);
            }
        });
    }
    setControl(formGroup: FormGroup, controlName: string, defaultValue: any, validators?: any[]): void {
        if (formGroup.get(controlName)) {
            return;
        }
        const control = this.fb.control(defaultValue, validators);
        formGroup.addControl(controlName, control);
    }
    removeControls(formGroup: FormGroup, controlNames: string[]): void {
        controlNames.forEach(controlName => {
            if (formGroup.get(controlName)) {
                formGroup.removeControl(controlName);
            }
        });
    }
    filterAndSetValue(control: AbstractControl | null) {
        const value = control?.value;
        if (value !== undefined && value !== null) {
            const filteredValue = value.toString().replace(/^0+(?=\d)/, ''); // Remove leading zeros
            const intValue = parseInt(filteredValue, 10) || 0; // Convert to integer, default to 0 if NaN
            intValue.toString() !== filteredValue && control?.setValue(intValue); // Set the filtered and converted value
        }
    };
    updateEmptyValuesToNull(obj: { [key: string]: any }): { [key: string]: any } {
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                if (obj[key] === '' || obj[key] === "" || obj[key] === undefined) {
                    obj[key] = null;
                }
            }
        }
        return obj;
    }
    // getLabels(formObject: { [key: string]: any }, filterObject: { [key: string]: any }): { [key: string]: any } {
    //   console.log(formObject, filterObject)
    //   const filteredObject: { [key: string]: any } = {};
    //   for (const key in filterObject) {
    //     if (filterObject.hasOwnProperty(key) && formObject.hasOwnProperty(key)) {
    //       filteredObject[key] = formObject[key];
    //     }
    //   }
    //   console.log(formObject, filterObject)
    //   return filteredObject;
    // }
    getLabels(data: any, filterArray: { key: string; title: string }[]): any[] {
        const filteredData: any[] = []; // Initialize as an array instead of an object
        filterArray.forEach(filter => {
            const key = filter.key;
            if (data.hasOwnProperty(key) && data[key] !== null && data[key] !== undefined) {
                filteredData.push({
                    key: key,
                    value: data[key],
                    title: filter.title,
                    total: 0
                });
            }
        });
        return filteredData;
    }
    getInt(value: string): number {
        if (typeof value === 'string') {
            //const numericString = value.replace(/\D/g, ''); // Remove non-numeric characters
            const withoutCommas = value.replaceAll(',', '');
            return parseFloat(withoutCommas); // Parse the result as an integer
        } else {
            return parseFloat(value); // If the input is not a string, return it as is
        }
    }

    getCommaSeparate(value: any): string {
        if (value !== null && value !== undefined) {
            const separateValue = value.toLocaleString('en-IN');
            return separateValue;
        } else {
            return '0';
        }
    }
    copyContent(content: string): void {
        const input = document.createElement('textarea');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        input.value = content;
        document.body.appendChild(input);
        input.focus();
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
    }
    color(color: string, opacity: number): string {
        color = color.substring(1);
        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    initializeTooltips() {
        this.destroyTooltips();
        setTimeout(() => {
            const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
            tooltipTriggerList.forEach((tooltipTriggerEl) => {
                const tooltipInstance = new bootstrap.Tooltip(tooltipTriggerEl);
                this.tooltipInstances.push(tooltipInstance);
            });
        }, 500);
    }

    tooltipInstances = [];
    destroyTooltips() {
        this.tooltipInstances.forEach((tooltipInstance) => {
            tooltipInstance.dispose();
        });
        this.tooltipInstances = [];
    }

    /**
     * Call right after a document upload completes for a form control.
     * Uploading a file without a value doesn't make sense, so the field
     * becomes required for as long as the file is attached — even if it
     * was optional on its own (e.g. a category/Yes-No dropdown with
     * required:false). Marking touched makes the error show immediately
     * instead of waiting for a separate focus/blur.
     * Shared by every citizen-attributedetail category's submitDocument().
     */
    markUploadedControlTouched(formGroup: FormGroup, field: string, fieldMeta?: { required?: boolean; pattern?: string }): void {
        const control = formGroup.get(field);
        if (!control) return;

        if (fieldMeta && !fieldMeta.required) {
            const validators = [Validators.required];
            if (fieldMeta.pattern) {
                validators.push(Validators.pattern(fieldMeta.pattern));
            }
            control.setValidators(validators);
        }

        control.markAsTouched();
        control.updateValueAndValidity();
    }

    /**
     * Call when a previously uploaded document is removed. Undoes the
     * "required while a file is attached" rule above by restoring the
     * control's validators back to the field's own natural required/pattern
     * rules. Shared by every citizen-attributedetail category's
     * clearUploadedFile().
     */
    resetControlValidatorsAfterClear(formGroup: FormGroup, field: string, fieldMeta: { required?: boolean; pattern?: string }): void {
        const control = formGroup.get(field);
        if (!control) return;

        const validators = [];
        if (fieldMeta.required) validators.push(Validators.required);
        if (fieldMeta.pattern) validators.push(Validators.pattern(fieldMeta.pattern));

        control.setValidators(validators);
        control.updateValueAndValidity();
    }

    /**
     * For a Yes/No dropdown that has its own "Upload Document" button beside it:
     * hide the button when the dropdown is set to the "No" value, keep it visible
     * for "Yes" or when nothing is selected yet.
     * hiddenValue defaults to 0 (this codebase's convention: {value:1,label:'Yes'} / {value:0,label:'No'}).
     */
    isUploadVisibleForFlag(formGroup: FormGroup, field: string, isUploadBtnVisible: boolean, hiddenValue: any = 0): boolean {
        return !!isUploadBtnVisible && formGroup.get(field)?.value !== hiddenValue;
    }

    registerFormmarkFormGroupTouched(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach(controlName => {
            const control = formGroup?.get(controlName);
            if (control instanceof FormControl) {
                if (!control.touched) {
                    control.markAsTouched();
                    control.updateValueAndValidity();
                }
            } else if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            } else if (control instanceof FormArray) {
                this.markFormArrayTouched(control);
            }
        });

        // Check for specific form group level validations like password match
        if (formGroup.errors && formGroup.errors['passwordsMismatch']) {
            const newPasswordControl = formGroup.get('newPassword');
            const confirmPasswordControl = formGroup.get('confirmPassword');
            if (newPasswordControl && confirmPasswordControl) {
            newPasswordControl.setErrors({ passwordsMismatch: true });
            confirmPasswordControl.setErrors({ passwordsMismatch: true });
            }
        }
    }
}
